import {
    Alarm,
    AlarmsCollectionResult,
    configAlarmsColumns,
} from '@services/alarms'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { Table, TableProps } from '@components/table'
import { useAppDispatch, useAppSelector } from '@hooks/store'

import { Query } from '@services/api'
import { setAlarmsQuery } from '@store/alarms'
import { useNavigate } from 'react-router-dom'
import { css } from '@emotion/react'
import { useEuiTheme } from '@elastic/eui'

type AlarmsTableProps = {
    data: AlarmsCollectionResult | undefined
    loading?: boolean
}

const AlarmsTable: FC<AlarmsTableProps> = ({ data, loading }) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const { euiTheme } = useEuiTheme()
    const { prefrences, query } = useAppSelector((state) => state.alarms)

    const columns = useMemo(() => configAlarmsColumns(prefrences), [prefrences])

    const defaultSort = useMemo(() => {
        const sorts = {
            field: 'created',
            direction: 'desc' as TableProps<Alarm>['sortDirection'],
        }

        if (query?.sorts?.length) {
            const sort = query.sorts[0]

            if (sort.indexOf('-') !== -1) {
                sorts.field = sort.substring(1)
                sorts.direction = 'desc'
            } else {
                sorts.field = sort
                sorts.direction = 'asc'
            }
        }

        return sorts
    }, [query])

    const getRowStyle = useCallback((record: Alarm) => {
        if (record.isAcknowledged || record.isOk) return undefined

        switch (record.status) {
            case 'Error':
                return css`
                    background-color: ${euiTheme.colors.backgroundBaseDanger};
                `
            case 'Warning':
                return css`
                    background-color: ${euiTheme.colors.backgroundBaseWarning};
                `
            default:
                return undefined
        }
    }, [])

    const handleOnQueryChange = (query: Query) =>
        dispatch(setAlarmsQuery({ ...query }))

    useEffect(() => {
        // Reset the page to 1, if there is no longer data on the page number from the query in the store
        if (
            data?.totalCount &&
            data?.totalCount > 0 &&
            query.page &&
            query.pageSize &&
            (query.page - 1) * query.pageSize > data?.totalCount
        )
            dispatch(setAlarmsQuery({ ...query, page: 1 }))
    }, [data])

    return (
        <Table<Alarm>
            items={data?.items || []}
            totalCount={data?.totalCount || 0}
            onChange={handleOnQueryChange}
            onRowClick={(record) => {
                navigate(`/alarms/${record.id}`)
            }}
            rowCss={getRowStyle}
            loading={loading}
            columns={columns}
            compressed={prefrences?.compressedLayout}
            layout="auto"
            sortField={defaultSort.field}
            sortDirection={defaultSort.direction}
        />
    )
}

export default AlarmsTable
